// ConsultationWizard.tsx
import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { useConsultationWizardState } from "../../hooks/useConsultationWizardState";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { consultationService } from "../../services/consultationService";
import { useConstanteList } from "../../hooks/useConstanteList";
import { StepNavigation } from "../../components/consultation/ComponentsConsultation/StepNavigation";
import { StatusMessages } from "../../components/consultation/ComponentsConsultation/StatusMessages";
import { ConsultationFormStep } from "../../components/consultation/ComponentsConsultation/ConsultationFormStep";
import { ConsultationSummary } from "../../components/consultation/ComponentsConsultation/ConsultationSummary";
import { Modal } from "../../components/ui/Modal";
import { ModalHeader } from "../../components/ui/ModalHeader";
import PrescriptionView from "../../components/documents/PrescriptionView";
import type { ConsultationWizardProps } from "../../types/consultation";
import { VisitReason } from "../../types";
import { toast } from "sonner";
import type { Medication } from "../../types/medication";
import { useUserProfile } from "../../hooks/useUserProfile";
import { pdf } from "@react-pdf/renderer";
import PrescriptionPDF from "../../pdf/PrescriptionPDF";
import AnalysesPDF from "../../pdf/AnalysesPDF";
import MedicalRecordPDF from "../../pdf/MedicalRecordPDF";

const ConsultationWizard: React.FC<ConsultationWizardProps> = ({
  patientId,
  patientName,
  role,
  consultationFee = 0,
  amountPaid = 0,
  constante,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const constanteData = constante || location.state?.constante;
  const { refetch } = useConstanteList(role);
  const { userData } = useUserProfile();

  // Log pour déboguer les données utilisateur

  const { handleError } = useErrorHandler();
  const [step, setStep] = useState<number>(1);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [prescriptionNumber, setPrescriptionNumber] = useState<string | undefined>(undefined);
  const [savedPrescription, setSavedPrescription] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Ref pour la prescription
  const prescriptionDocumentRef = useRef<HTMLDivElement>(null);

  const {
    formData,
    analyses,
    medications,
    resetWizard,
  } = useConsultationWizardState(constanteData);

  const handleNext = () => setStep((prev) => prev + 1);

  const handleBackStep = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    } else {
      resetWizard(); // Reset state when leaving
      navigate(`/${role}/dashboard/constante`);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const patientNumericId = parseInt(patientId, 10);
      const motifConsultation =
        (formData?.patientInfo as any)?.motifConsultation || "";
      const normalized = motifConsultation.toString().toLowerCase();
      const isPreEmployment =
        motifConsultation === "PRE_EMPLOYMENT_VISIT" ||
        normalized.includes("pré-embauche") ||
        normalized.includes("pre-embauche") ||
        normalized.includes("embauche");


      const { medicalRecord, prescription } =
        await consultationService.createConsultationAuto(
          { ...formData, medications, analyses },
          patientNumericId,
          consultationFee,
          amountPaid
        );


      // Mettre à jour l'état avec la prescription
      if (prescription?.orderNumber) {
        setPrescriptionNumber(prescription.orderNumber);
      }

      try {
        if (constanteData?.id) {
          await consultationService.markConstanteAsConsulted(
            constanteData.id,
            medicalRecord.id,
            isPreEmployment
              ? VisitReason.PRE_EMPLOYMENT_VISIT
              : VisitReason.GENERAL_CONSULTATION
          );
        }
      } catch (linkErr) {
        console.warn(
          "Impossible de lier la consultation à la constante:",
          linkErr
        );
      }

      setSaveSuccess(true);
      setSavedPrescription({ consultationId: medicalRecord?.id, ...prescription });

      // Vérifier s'il y a des médicaments à afficher
      const hasMedications = medications && medications.length > 0;

      if (hasMedications) {
        // Cas 1: Il y a des médicaments - on affiche le modal et génère le PDF
        setShowSuccessModal(true);

        // Auto-download prescription
        try {
          const orderNumber = prescription?.orderNumber || `ORD-${medicalRecord.id}-${Date.now()}`;
          const blob = await pdf(
            <PrescriptionPDF
              medications={medications as Medication[]}
              patientInfo={{
                nom: formData?.patientInfo?.nom || "",
                prenom: formData?.patientInfo?.prenom || "",
                dateNaissance: formData?.patientInfo?.dateNaissance || "",
              }}
              doctorInfo={{
                firstName: userData?.firstName || "",
                lastName: userData?.lastName || "",
                title: "DR",
              }}
              consultationDate={new Date().toISOString()}
              orderNumber={orderNumber}
              instructions={formData.consultationActuelle.recommandations}
            />
          ).toBlob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `ordonnance_${formData?.patientInfo?.nom}_${orderNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (downloadErr) {
          console.error("Erreur téléchargement auto ordonnance:", downloadErr);
          toast.error("Échec du téléchargement automatique de l'ordonnance.");
        }
      } else {
        // Cas 2: Pas de médicaments ou pas de prescription - on redirige
        // Pas de toast, génération silencieuse
      }

      // Auto-download analyses if any
      if (analyses && analyses.length > 0) {
        try {
          // Extraire et combiner toutes les instructions des analyses
          const allInstructions = analyses
            .filter(a => a.conseils && a.conseils.trim())
            .map(a => a.conseils)
            .join('\n');

          const blob = await pdf(
            <AnalysesPDF
              analyses={analyses}
              patientInfo={{
                nom: formData?.patientInfo?.nom || "",
                prenom: formData?.patientInfo?.prenom || "",
                dateNaissance: formData?.patientInfo?.dateNaissance || "",
              }}
              doctorInfo={{
                firstName: userData?.firstName || "",
                lastName: userData?.lastName || "",
                title: "DR",
              }}
              consultationDate={new Date().toISOString()}
              instructions={allInstructions} // ✅ Ajouter toutes les instructions combinées
            />
          ).toBlob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `analyses_${formData?.patientInfo?.nom}_${new Date().getTime()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (downloadErr) {
          console.error("Erreur téléchargement auto analyses:", downloadErr);
          toast.error("Échec du téléchargement automatique des analyses.");
        }
      }

      // ✅ Auto-download medical record
      try {
        // Créer les données du dossier médical à partir du formulaire
        const medicalRecordData = {
          id: savedPrescription?.consultationId || Date.now(),
          doctorId: Number(userData?.id) || 0,
          patientId: parseInt(patientId, 10),
          patientFullName: `${formData?.patientInfo?.nom} ${formData?.patientInfo?.prenom}`,
          consultationFee: consultationFee || 0,
          amountPaid: amountPaid || 0,
          amountDue: (consultationFee || 0) - (amountPaid || 0),
          dateCreation: new Date().toISOString(),
          symptoms: formData?.patientInfo?.dureeSymptomes || "",
          diagnostic: formData?.consultationActuelle?.diagnostics || "",
          observations: formData?.consultationActuelle?.observations || "",
          recommendation: formData?.consultationActuelle?.recommandations || "",
          // Ajouter les champs optionnels pour compatibilité
          phone: formData?.patientInfo?.telephone || "",
          doctorName: `${userData?.firstName} ${userData?.lastName}`,
          symptomDuration: formData?.patientInfo?.dureeSymptomes || "",
          allergies: formData?.patientInfo?.allergies || "",
          preexistingCondition: Object.keys(formData?.conditionsMedicales || {})
            .filter(key => formData?.conditionsMedicales[key] === true)
            .join(', '),
          familyHistory: Object.keys(formData?.antecedentsFamiliaux || {})
            .filter(key => formData?.antecedentsFamiliaux[key] === true)
            .join(', '),
          // Ajouter les examens additionnels si existants
          additionalExaminations: analyses?.map((analyse, index) => ({
            examName: analyse.nom || `Examen ${index + 1}`,
            reason: analyse.reason || "Demande d'analyse",
            concernedDiagnostics: formData?.consultationActuelle?.diagnostics || "",
            patientAdvice: "Présenter ce document au laboratoire"
          })) || [],
          // Ajouter les médicaments si existants
          prescriptionItems: medications?.map(med => ({
            name: med.name,
            quantity: med.quantity,
            quantityUnit: med.quantityUnit,
            dose: med.dose.toString(),
            doseUnit: med.doseUnit,
            frequency: med.frequency,
            interval: med.interval,
            duration: med.duration,
            durationUnit: med.durationUnit,
            startDate: med.startDate,
            endDate: med.endDate,
            advice: med.advice,
            instructions: med.instructions
          })) || [],
        };

        const blob = await pdf(
          <MedicalRecordPDF
            record={medicalRecordData}
            patientPhone={formData?.patientInfo?.telephone}
            doctorFullName={`${userData?.firstName} ${userData?.lastName}`}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dossier_medical_${formData?.patientInfo?.nom}_${new Date().getTime()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (downloadErr) {
        console.error("Erreur téléchargement auto dossier médical:", downloadErr);
        toast.error("Échec du téléchargement automatique du dossier médical.");
      }

      // Navigation après tous les téléchargements
      navigateToDashboard();
      refetch?.();
    } catch (err: unknown) {
      const errorMessage = handleError(err);
      setSaveError(errorMessage);
      toast.error(errorMessage);
      console.error("Erreur lors de la création de la consultation:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const navigateToDashboard = (prescription?: any) => {
    const motifConsultation = (formData?.patientInfo as any)?.motifConsultation || "";
    const normalized = motifConsultation.toString().toLowerCase();
    const isPreEmployment =
      motifConsultation === "PRE_EMPLOYMENT_VISIT" ||
      normalized.includes("pré-embauche") ||
      normalized.includes("pre-embauche") ||
      normalized.includes("embauche");

    if (isPreEmployment && role === "doctor") {
      navigate(`/doctor/dashboard/certificat-pre-embauche`, {
        state: {
          consultationData: formData,
          medicalRecord: { id: savedPrescription?.consultationId },
          patientId: parseInt(patientId, 10),
        },
      });
    } else {
      navigate(`/${role}/dashboard/constante`, {
        state: {
          message: prescription
            ? "Consultation et prescription créées avec succès!"
            : "Consultation créée avec succès!",
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100 gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Nouvelle Consultation
              </h1>
              <p className="text-gray-600 mt-1">
                Remplissez les informations pour {patientName}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBackStep}
                className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-1"
              >
                <ArrowLeft size={18} />
                Retour
              </button>
            </div>
          </div>
        </div>

        <StatusMessages
          loading={isSaving}
          error={saveError}
          success={saveSuccess}
        />

        <div className="bg-white rounded-xl shadow-lg p-6 space-y-8">
          {step === 1 && (
            <ConsultationFormStep
              onNext={handleNext}
              onBack={handleBackStep}
              constanteData={constanteData}
            />
          )}
          {step === 2 && (
            <ConsultationSummary
              onBack={handleBackStep}
              onSave={handleSave}
              loading={isSaving}
            />
          )}
          <StepNavigation
            currentStep={step}
            totalSteps={2}
            onPrevious={step > 1 ? handleBackStep : undefined}
            onNext={step === 1 ? handleNext : handleSave}
            nextLabel={step === 1 ? "Voir le récapitulatif" : "Enregistrer"}
            showPrevious={step > 1}
            loading={isSaving}
          />
        </div>
      </div>

      {/* Modal de succès avec accès à l'ordonnance */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          // Rediriger vers le tableau de bord après la fermeture du modal
          navigateToDashboard();
        }}
        maxWidth="max-w-2xl"
      >
        <div className="p-8">
          <ModalHeader
            title="Consultation enregistrée avec succès!"
            icon={FileText}
            iconColor="text-green-600"
            onClose={() => setShowSuccessModal(false)}
          />

          <div className="mt-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Ordonnance générée
                  </h3>
                  <div className="flex flex-col">
                    <span className="text-sm font-mono text-gray-800">
                      {prescriptionNumber}
                    </span>
                    <span className="text-xs text-gray-500">
                      {prescriptionNumber && prescriptionNumber.includes("-")
                        ? (() => {
                          const parts = prescriptionNumber.split("-");
                          if (parts.length === 5) {
                            const [, year, seq, patient, doctor] = parts;
                            return `Année ${year} • Séquence ${seq} • Patient ${patient} • Dr ${doctor}`;
                          }
                          return "";
                        })()
                        : ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Affichage de l'ordonnance */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <PrescriptionView
                  ref={prescriptionDocumentRef}
                  medications={medications as Medication[]}
                  patientInfo={{
                    nom: formData?.patientInfo?.nom || "",
                    prenom: formData?.patientInfo?.prenom || "",
                    dateNaissance: formData?.patientInfo?.dateNaissance || "",
                  }}
                  doctorInfo={{
                    firstName: userData?.firstName || "",
                    lastName: userData?.lastName || "",
                    title: "DR",
                  }}
                  consultationDate={new Date().toISOString()}
                  orderNumber={prescriptionNumber}
                  instructions={formData.consultationActuelle.recommandations}
                  onBack={() => setShowSuccessModal(false)}
                  onPrint={() => {
                    if (prescriptionDocumentRef.current) {
                      window.print();
                    }
                  }}
                  onDownloadPDF={() => {
                    // PDF download is handled by PrescriptionView's internal button
                  }}
                />
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigateToDashboard();
                }}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200 font-medium"
              >
                Continuer vers le tableau de bord
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ConsultationWizard;
